import useEntityStore from "../../stores/entityStore";
import { createFileLogger } from "../../utils/logger";

const log = createFileLogger('ENTITY_ID_RESOLVER', { enabled: false });

/**
 * Resolves an entity ID that could be either a MongoDB _id or an llmId
 * to the canonical MongoDB _id.
 * 
 * The AI sometimes provides llmId (e.g., "note_red_cave_deep_midnight") 
 * instead of _id (e.g., "6930c409dc0a3aca82a59ebf").
 * 
 * This function handles both cases transparently.
 */
export function resolveEntityId(entityId: string): { 
  resolved: string; 
  isLlmId: boolean;
  entity?: any;
} {
  // Check if this looks like an llmId (contains underscores, not a hex string)
  const isLlmId = entityId.includes('_') && !/^[0-9a-f]{24}$/i.test(entityId);
  
  if (!isLlmId) {
    // Already a MongoDB _id, return as-is
    return { resolved: entityId, isLlmId: false };
  }
  
  // Need to resolve llmId to _id
  log.info('[resolveEntityId] Resolving llmId to _id:', { llmId: entityId });
  
  const entityStore = useEntityStore.getState();
  const entity = entityStore.entities.find(e => e.llmId === entityId);
  
  if (!entity) {
    // Could not resolve - return original ID and let caller handle the error
    log.warn('[resolveEntityId] Could not resolve llmId:', { llmId: entityId });
    return { resolved: entityId, isLlmId: true };
  }
  
  log.info('[resolveEntityId] Resolved llmId to _id:', { llmId: entityId, _id: entity._id });
  return { resolved: entity._id, isLlmId: true, entity };
}

/**
 * Resolve multiple entity IDs (for batch operations)
 */
export function resolveEntityIds(entityIds: string[]): {
  resolved: string[];
  failed: string[];
  entities: any[];
} {
  const resolved: string[] = [];
  const failed: string[] = [];
  const entities: any[] = [];
  
  for (const entityId of entityIds) {
    const result = resolveEntityId(entityId);
    
    if (result.entity || !result.isLlmId) {
      resolved.push(result.resolved);
      if (result.entity) {
        entities.push(result.entity);
      }
    } else {
      failed.push(entityId);
    }
  }
  
  return { resolved, failed, entities };
}



